import axios from 'axios';
import axiosInstance from './api';
import { blobToCoverImageFile } from '../components/Events/ManageEvent/coverImageUtils';
import {
  PARTNER_MEDIA_OPTIMIZATION_TIMEOUT_MS,
  REGENERATE_PARTNER_AVATAR_FIELD,
  REGENERATE_PARTNER_COVER_BANNER_FIELD,
} from '../components/PartnerSettings/PartnerSettingsSection/companyInfoDefaults';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (axios.isCancel(error)) return fallback;
  if (typeof error === 'string') return error;
  return error.message || error?.error?.message || error?.data?.message || fallback;
};

const parseBlobErrorMessage = async (blob) => {
  try {
    const text = await blob.text();
    const payload = JSON.parse(text);
    return payload?.message || payload?.error?.message || null;
  } catch {
    return null;
  }
};

const regeneratePartnerImageFromFile = async (
  file,
  { endpoint, field, fileName, fallbackMessage, signal } = {},
) => {
  if (!(file instanceof File)) {
    throw new Error('Please select an image to upload.');
  }

  const formData = new FormData();
  formData.append(field, file);

  try {
    const response = await axiosInstance.post(endpoint, formData, {
      responseType: 'blob',
      signal,
      timeout: PARTNER_MEDIA_OPTIMIZATION_TIMEOUT_MS,
    });

    const blob = response.data;
    const contentType = String(response.headers?.['content-type'] ?? '');

    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error('Empty image returned from server.');
    }

    if (contentType.includes('application/json')) {
      const message = (await parseBlobErrorMessage(blob)) || fallbackMessage;
      throw new Error(message);
    }

    return blobToCoverImageFile(blob, fileName);
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    throw new Error(getApiErrorMessage(error, fallbackMessage));
  }
};

/** POST /admin/partner/regenerate-avatar — multipart; returns 810×810 PNG. */
export const regeneratePartnerAvatarFromFile = async (file, { signal } = {}) =>
  regeneratePartnerImageFromFile(file, {
    endpoint: '/admin/partner/regenerate-avatar',
    field: REGENERATE_PARTNER_AVATAR_FIELD,
    fileName: 'partner-avatar.png',
    fallbackMessage: 'Failed to optimize logo.',
    signal,
  });

/** POST /admin/partner/regenerate-cover-banner — multipart; returns 1536×1024 PNG. */
export const regeneratePartnerCoverBannerFromFile = async (file, { signal } = {}) =>
  regeneratePartnerImageFromFile(file, {
    endpoint: '/admin/partner/regenerate-cover-banner',
    field: REGENERATE_PARTNER_COVER_BANNER_FIELD,
    fileName: 'partner-cover-banner.png',
    fallbackMessage: 'Failed to optimize cover banner.',
    signal,
  });
